import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTranslations } from "use-intl";
import { Link } from "react-router";
import {
  BookHeartIcon,
  ChartBarIcon,
  ChevronRightIcon,
  CreditCardIcon,
  FolderHeartIcon,
  LayoutIcon,
  MailIcon,
  QrCodeIcon,
  RowsIcon,
  Settings2Icon,
  SettingsIcon,
  ShieldIcon,
  UsersIcon,
} from "lucide-react";
import { NavUser } from "./nav-user";
import { OrganizationSwitcher } from "./organization-switcher";
import useAuth from "@/shared/hooks/use-auth";
import useTheme from "@/shared/hooks/use-theme";
import logoInverted from "@/assets/logo-inverted.svg";
import logo from "@/assets/logo.svg";

const coreLinks = [
  {
    title: "sidebar.dashboard",
    href: "/admin/dashboard",
    icon: <LayoutIcon size={18} />,
  },
  {
    title: "sidebar.users",
    href: "/admin/users",
    icon: <UsersIcon size={18} />,
  },
  {
    title: "sidebar.qr-codes",
    href: "/admin/qr-codes",
    icon: <QrCodeIcon size={18} />,
  },
];

const insightsLinks = [
  {
    title: "sidebar.insights",
    href: "/admin/insights",
    icon: <ChartBarIcon size={18} />,
  },
];

const communicationsLinks = [
  {
    title: "sidebar.communications",
    href: "/admin/communications",
    icon: <MailIcon size={18} />,
  },
];

const loyaltyLinks = [
  {
    title: "sidebar.loyalty-cards.title",
    href: "/admin/loyalty-cards",
    icon: <BookHeartIcon size={18} />,
    sub: [
      {
        title: "sidebar.loyalty-cards.stamp-cards",
        href: "/admin/loyalty-cards/stamp-cards",
        icon: <BookHeartIcon size={18} />,
      },
      {
        title: "sidebar.loyalty-cards.gift-cards",
        href: "/admin/loyalty-cards/gift-cards",
        icon: <BookHeartIcon size={18} />,
      },
    ],
  },
  {
    title: "sidebar.loyalty-program",
    href: "/admin/loyalty-program",
    icon: <FolderHeartIcon size={18} />,
    sub: [
      {
        title: "sidebar.loyalty-program",
        href: "/admin/loyalty-program",
        icon: <FolderHeartIcon size={18} />,
      },
      {
        title: "sidebar.loyalty-levels",
        href: "/admin/loyalty-program/levels",
        icon: <RowsIcon size={18} />,
      },
    ],
  },
];

const settingsLinks = [
  {
    title: "sidebar.settings.title",
    href: "/admin/settings",
    icon: <SettingsIcon size={18} />,
    sub: [
      {
        title: "sidebar.settings.general",
        href: "/admin/settings",
        icon: <Settings2Icon size={18} />,
      },
      {
        title: "sidebar.settings.administrators",
        href: "/admin/settings/administrators",
        icon: <ShieldIcon size={18} />,
      },
      {
        title: "sidebar.settings.subscription",
        href: "/admin/subscription",
        icon: <CreditCardIcon size={18} />,
      },
    ],
  },
];

export function AppSidebar() {
  const t = useTranslations();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();

  if (!user) return null;

  return (
    <Sidebar collapsible="icon" className="transition-all duration-300">
      <SidebarHeader>
        <div className="flex items-center gap-3 px-3 py-3 group-data-[collapsible=icon]:justify-center">
          <img
            src={isDarkMode ? logoInverted : logo}
            alt="Lojal Logo"
            className="h-8 w-8 flex-shrink-0 group-data-[collapsible=icon]:min-w-[2rem] group-data-[collapsible=icon]:min-h-[2rem] group-data-[collapsible=icon]:shrink-0 group-data-[collapsible=icon]:mx-auto"
          />
          <div className="flex flex-col group-data-[collapsible=icon]:hidden">
            <span className="text-lg font-semibold">{t("adminApp.label")}</span>
            <span className="text-xs text-muted-foreground">
              {t("adminApp.tagline")}
            </span>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {/* Core Features */}
        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
            {t("sidebar.groups.core")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {coreLinks.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.href} className="flex items-center gap-3">
                      {item.icon}
                      <span className="group-data-[collapsible=icon]:hidden">
                        {t(item.title)}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Analytics */}
        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
            {t("sidebar.groups.insights")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {insightsLinks.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.href} className="flex items-center gap-3">
                      {item.icon}
                      <span className="group-data-[collapsible=icon]:hidden">
                        {t(item.title)}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Communications */}
        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
            {t("sidebar.groups.communications")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {communicationsLinks.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.href} className="flex items-center gap-3">
                      {item.icon}
                      <span className="group-data-[collapsible=icon]:hidden">
                        {t(item.title)}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Loyalty System */}
        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
            {t("sidebar.groups.loyalty")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {loyaltyLinks.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <Collapsible className="group/collapsible">
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton className="cursor-pointer flex items-center gap-3">
                        {item.icon}
                        <span className="group-data-[collapsible=icon]:hidden">
                          {t(item.title)}
                        </span>
                        <ChevronRightIcon className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.sub?.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton asChild>
                              <Link
                                to={subItem.href}
                                className="flex items-center gap-3"
                              >
                                {subItem.icon}
                                <span className="group-data-[collapsible=icon]:hidden">
                                  {t(subItem.title)}
                                </span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </Collapsible>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Settings (organization) */}
        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
            {t("sidebar.groups.settings")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {settingsLinks.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <Collapsible className="group/collapsible">
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton className="cursor-pointer flex items-center gap-3">
                        {item.icon}
                        <span className="group-data-[collapsible=icon]:hidden">
                          {t(item.title)}
                        </span>
                        <ChevronRightIcon className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.sub?.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton asChild>
                              <Link
                                to={subItem.href}
                                className="flex items-center gap-3"
                              >
                                {subItem.icon}
                                <span className="group-data-[collapsible=icon]:hidden">
                                  {t(subItem.title)}
                                </span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </Collapsible>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem className="flex flex-col gap-2">
            <OrganizationSwitcher />
            <NavUser user={user} />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
